// The newsroom's judgement, as plain functions over what the page holds (no fetch, no clock but
// the one passed in, no DOM):
//
//   candles ──> moves: windows where a market moved far more than it usually does
//   moves × wire ──> causes: the headlines most likely behind each move, with their evidence
//   moves + causes ──> stories: markets that moved together for one reason, ranked by impact
//   wire × candles ──> reactions (what each headline has done to its markets since) and the
//                      headlines still waiting on a reaction
//
// Every judgement is a prior and says so: a cause is a headline that fits the move in time and
// topic, not proof. Prices here are floats for measuring, never for money.

import type { AssetClass, Place, WireItem } from "../../shared/newsroom/types.ts";
import type { Market, Universe } from "./universe";
import type { Bar, Series } from "./candles";

// ---------------------------------------------------------------- moves

export type Move = {
  id: string;
  coin: string;
  /** The first bar's open time and the last bar's close time, ms. */
  start: number;
  end: number;
  from: number;
  to: number;
  /** Fractional return over the window. */
  ret: number;
  /** The return in units of the market's own volatility over that span, signed. */
  z: number;
  bars: number;
  /** Dollars traded in the window. */
  notional: number;
  /** The window's volume per bar against the day's median bar. */
  volumeRatio: number;
  /** Still moving: the window ends in the current bar. */
  ongoing: boolean;
};

const BAR_MS = 300_000;
/** Window lengths in bars: 5 min to 2 h. */
const SPANS = [1, 2, 3, 6, 12, 24];
const Z_MIN = 4;
/** The least move worth a story, by class: a 0.5% day in FX is news, in crypto it is noise. */
const MIN_RET: Record<AssetClass, number> = {
  crypto: 0.009,
  equities: 0.006,
  commodities: 0.005,
  fx: 0.0015,
  rates: 0.004,
  indices: 0.0035,
};
/** The least traded in the window for it to count. */
const MIN_NOTIONAL = 40_000;
/** A 5-minute volatility floor, so a market that barely trades cannot divide by nothing. */
const SIGMA_FLOOR = 0.00015;
/** The least a market trades in a day for its moves to be news, by class. */
const MIN_DAY_VOLUME: Record<AssetClass, number> = {
  crypto: 2_000_000,
  equities: 300_000,
  commodities: 300_000,
  fx: 100_000,
  rates: 50_000,
  indices: 300_000,
};
/** Past this many times its usual move, how unusual a move is stops adding to its rank. */
const Z_CAP = 30;
const zOf = (move: Move) => Math.min(Z_CAP, Math.abs(move.z));

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? (sorted[mid] as number) : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

/** A robust 5-minute volatility: 1.4826 × the median absolute deviation of log returns. */
export function sigmaOf(bars: Bar[]): number {
  const returns: number[] = [];
  for (let i = 1; i < bars.length; i++) {
    const a = (bars[i - 1] as Bar).c;
    const b = (bars[i] as Bar).c;
    if (a > 0 && b > 0) returns.push(Math.log(b / a));
  }
  const center = median(returns);
  const mad = median(returns.map((r) => Math.abs(r - center)));
  return Math.max(SIGMA_FLOOR, 1.4826 * mad);
}

/** The strongest separate moves of one market over its series, strongest first. */
export function detect(market: Market, bars: Bar[], now: number, keep = 3): Move[] {
  if (bars.length < 30 || market.dayVolumeUsd < MIN_DAY_VOLUME[market.class]) return [];
  // a price that sits still in most bars is a quote that jumps, not a market that moves
  let still = 0;
  for (let i = 1; i < bars.length; i++) if ((bars[i] as Bar).c === (bars[i - 1] as Bar).c) still += 1;
  if (still / (bars.length - 1) > 0.45) return [];
  const sigma = sigmaOf(bars);
  const minRet = MIN_RET[market.class];
  const volumes = bars.map((bar) => bar.v);
  const meanVolume = volumes.reduce((sum, v) => sum + v, 0) / volumes.length;
  // a thin market's median bar can be empty: a fifth of the mean keeps the ratio honest
  const medianVolume = Math.max(median(volumes), meanVolume * 0.2, 1e-12);
  const candidates: Move[] = [];
  for (const k of SPANS) {
    for (let end = k; end < bars.length; end++) {
      const before = bars[end - k] as Bar;
      const last = bars[end] as Bar;
      if (!(before.c > 0 && last.c > 0)) continue;
      const logRet = Math.log(last.c / before.c);
      const z = logRet / (sigma * Math.sqrt(k));
      if (Math.abs(z) < Z_MIN || Math.abs(Math.expm1(logRet)) < minRet) continue;
      let volume = 0;
      let notional = 0;
      for (let i = end - k + 1; i <= end; i++) {
        const bar = bars[i] as Bar;
        volume += bar.v;
        notional += bar.v * bar.c;
      }
      if (notional < MIN_NOTIONAL) continue;
      // a wick that gives the move straight back is a bad print, not news: what follows must keep
      // at least 40% of it (a move still running is kept)
      const after = bars.slice(end + 1, end + 4);
      if (after.length) {
        const kept = Math.min(...after.map((bar) => (logRet > 0 ? Math.log(bar.c / before.c) : -Math.log(bar.c / before.c))));
        if (kept < 0.4 * Math.abs(logRet)) continue;
      }
      // and the snap back from one is not a move either: from the bar before the wick, the price
      // must have gone somewhere
      const earlier = bars[end - k - 1];
      if (earlier && earlier.c > 0 && Math.abs(Math.log(last.c / earlier.c)) < 0.4 * Math.abs(logRet)) continue;
      const first = bars[end - k + 1] as Bar;
      candidates.push({
        id: `${market.coin}:${first.t}:${k}`,
        coin: market.coin,
        start: first.t,
        end: last.t + BAR_MS,
        from: before.c,
        to: last.c,
        ret: Math.expm1(logRet),
        z,
        bars: k,
        notional,
        volumeRatio: volume / k / medianVolume,
        ongoing: last.t + BAR_MS > now,
      });
    }
  }
  // the strongest first; a longer window has to beat a shorter one inside it clearly
  candidates.sort((a, b) => Math.abs(b.z) * Math.pow(b.bars, 0.15) - Math.abs(a.z) * Math.pow(a.bars, 0.15));
  const chosen: Move[] = [];
  for (const candidate of candidates) {
    if (chosen.some((held) => candidate.start < held.end && held.start < candidate.end)) continue;
    chosen.push(candidate);
    if (chosen.length >= keep) break;
  }
  return chosen;
}

// ---------------------------------------------------------------- causes

export type Cause = {
  item: WireItem;
  score: number;
  /** Minutes from the move's start to the headline: negative when the headline came first. */
  lagMin: number;
  /** Why it fits, in words: "names NVDA", "oil", "Strait of Hormuz", "macro", "3 min before". */
  reasons: string[];
};

export type Relevance = { score: number; reasons: string[] };

const MAJORS = new Set(["BTC", "ETH"]);

/** How much a headline is about a market, 0 to 1, and why. Naming the market is the strongest
 * tie; a family of markets is next; a region, a market-wide or crypto-wide story are weak on their
 * own. */
export function relevance(item: WireItem, market: Market): Relevance {
  const tags = item.tags;
  const u = market.underlying;
  if (tags.tickers.includes(u)) return { score: 1, reasons: [`names ${market.symbol}`] };
  const family = tags.families.find((f) => f.underlyings.includes(u));
  if (family) {
    const rank = family.underlyings.indexOf(u);
    return { score: rank === 0 ? 0.75 : 0.6, reasons: [family.tag.toLowerCase()] };
  }
  if (tags.place?.region?.syms.includes(u)) return { score: 0.5, reasons: [tags.place.region.name] };
  // news about crypto as a whole moves bitcoin and ether; one small token moving alone is its own story
  if (tags.cryptoWide && market.class === "crypto") return { score: MAJORS.has(u) ? 0.45 : 0.2, reasons: ["crypto-wide"] };
  if (tags.macro) {
    const reach =
      market.class === "indices" || market.class === "fx" || market.class === "rates" || u === "XAU"
        ? 0.45
        : market.class === "crypto"
          ? MAJORS.has(u)
            ? 0.35
            : 0.25
          : 0.3;
    return { score: reach, reasons: ["macro"] };
  }
  const classMatch = tags.families.find((f) => f.class === market.class);
  if (classMatch) return { score: 0.2, reasons: [classMatch.tag.toLowerCase()] };
  return { score: 0, reasons: [] };
}

/** How well a headline's time fits a move: news before the move counts most, a report of it after
 * still explains it, an old story barely. */
export function timeFit(lagMs: number, moveMs: number): number {
  const min = lagMs / 60_000;
  const span = moveMs / 60_000;
  if (min >= -15 && min <= span + 15) return 1;
  if (min < -15) return Math.max(0, Math.exp(-(-min - 15) / 30)) * (min < -240 ? 0 : 1);
  return Math.max(0, Math.exp(-(min - span - 15) / 25)) * (min > span + 120 ? 0 : 1);
}

function lagWords(lagMin: number): string {
  const m = Math.round(lagMin);
  if (Math.abs(m) <= 1) return "as it moved";
  return m < 0 ? `${-m} min before` : `${m} min in`;
}

// ---------------------------------------------------------------- the calendar

/** New York's offset from UTC in hours at `ms`: −4 from the second Sunday of March to the first
 * Sunday of November, −5 otherwise. */
export function newYorkOffset(ms: number): number {
  const date = new Date(ms);
  const year = date.getUTCFullYear();
  const nthSunday = (month: number, n: number) => {
    const first = new Date(Date.UTC(year, month, 1));
    const day = 1 + ((7 - first.getUTCDay()) % 7) + (n - 1) * 7;
    return Date.UTC(year, month, day, 7); // 2:00 in New York, as UTC
  };
  return ms >= nthSunday(2, 2) && ms < nthSunday(10, 1) - 3_600_000 ? -4 : -5;
}

type Scheduled = { id: string; title: string; ts: number; classes: AssetClass[] | "all"; weight: number };

/** The day's fixed hours: New York's cash open and close, and its 8:30 data window, on weekdays. */
export function scheduleAround(ms: number): Scheduled[] {
  const out: Scheduled[] = [];
  for (const dayOffset of [-1, 0]) {
    const day = new Date(ms + dayOffset * 86_400_000);
    const weekday = day.getUTCDay();
    if (weekday === 0 || weekday === 6) continue;
    const offset = newYorkOffset(day.getTime());
    const at = (h: number, m: number) => Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), h - offset, m);
    const key = day.toISOString().slice(0, 10);
    out.push(
      { id: `cal:data:${key}`, title: "US economic data window (8:30 New York)", ts: at(8, 30), classes: "all", weight: 0.35 },
      { id: `cal:open:${key}`, title: "US stock market open (9:30 New York)", ts: at(9, 30), classes: ["equities", "indices"], weight: 0.5 },
      { id: `cal:close:${key}`, title: "US stock market close (16:00 New York)", ts: at(16, 0), classes: ["equities", "indices"], weight: 0.45 },
    );
  }
  return out;
}

const NO_TAGS = { families: [], tickers: [], place: null, underlyings: [], macro: false, cryptoWide: false };

/** A fixed hour the move started on, as a cause: a weak prior, said as one. */
function calendarCause(move: Move, market: Market): Cause | null {
  for (const event of scheduleAround(move.start)) {
    if (event.classes !== "all" && !event.classes.includes(market.class)) continue;
    const lag = event.ts - move.start;
    if (Math.abs(lag) > 6 * 60_000) continue;
    const item: WireItem = { id: event.id, title: event.title, link: "", source: "Market calendar", ts: event.ts, lane: "markets", tags: NO_TAGS };
    return { item, score: event.weight, lagMin: lag / 60_000, reasons: ["scheduled", lagWords(lag / 60_000)] };
  }
  return null;
}

const COMMENTARY =
  /\b(?:technical (?:analysis|levels)|price (?:analysis|prediction|forecast)|key levels|levels to watch|what to watch|chart of the day|morning (?:kickstart|briefing|note)|week ahead|day ahead|preview|explainer|podcast|opinion)\b|\s\|\s[A-Z][\w.'-]+(?: [A-Z][\w.'-]+)+$/i;
/** Words that say a market moved, by direction: a headline reporting the move beats one that
 * merely names the market. */
const FELL = /\b(?:falls?|fell|dips?|dipped|drops?|dropped|slides?|slid|slips?|slipped|sinks?|sank|tumbles?|tumbled|plunges?|plunged|slumps?|slumped|sheds?|lower|declines?|declined|retreats?|crash\w*|sell-?off|rout|sinks?)\b/i;
const ROSE = /\b(?:rises?|rose|jumps?|jumped|surges?|surged|soars?|soared|rall(?:y|ies|ied)|climbs?|climbed|gains?|gained|higher|spikes?|spiked|pops?|popped|rebounds?|rebounded|advances?|advanced)\b/i;

export function causesOf(move: Move, market: Market, wire: WireItem[], limit = 5): Cause[] {
  const out: Cause[] = [];
  const scheduled = calendarCause(move, market);
  if (scheduled) out.push(scheduled);
  const events = scheduleAround(move.start);
  const near = (id: string) => events.find((event) => event.id.startsWith(id) && Math.abs(event.ts - move.start) <= 6 * 60_000);
  const release = near("cal:data");
  const open = market.class === "equities" || market.class === "indices" ? near("cal:open") : undefined;
  for (const item of wire) {
    const lag = item.ts - move.start;
    // news out overnight is priced when New York opens
    const overnight = open !== undefined && lag < 0 && lag >= -18 * 3_600_000;
    if (!overnight && (lag < -4 * 3_600_000 || lag > move.end - move.start + 2 * 3_600_000)) continue;
    const rel = relevance(item, market);
    if (rel.score === 0) continue;
    let score = rel.score * timeFit(lag, move.end - move.start);
    const reasons = [...rel.reasons, lagWords(lag / 60_000)];
    // a market-wide headline at a scheduled data release is the release
    if (release && item.tags.macro && Math.abs(item.ts - release.ts) <= 40 * 60_000) {
      score = Math.max(score, 0.8);
      reasons.splice(0, rel.reasons.length, "data release");
    }
    if (overnight && rel.score >= 0.75) {
      score = Math.max(score, rel.score * 0.9);
      reasons[reasons.length - 1] = "before the open";
    }
    // a chart read, a preview or a morning note is about the market, not something that happened to it
    if (COMMENTARY.test(item.title)) score *= 0.5;
    // a headline about this market that says it went the other way is about another moment
    const own = rel.reasons[0]?.startsWith("names") || rel.reasons[0] === "crypto-wide";
    if (own && (move.ret < 0 ? ROSE : FELL).test(item.title) && !(move.ret < 0 ? FELL : ROSE).test(item.title)) score *= 0.7;
    if (score < 0.12) continue;
    out.push({ item, score, lagMin: lag / 60_000, reasons });
  }
  const told = move.ret < 0 ? FELL : ROSE;
  out.sort((a, b) => b.score - a.score || Number(told.test(b.item.title)) - Number(told.test(a.item.title)) || Math.abs(a.lagMin) - Math.abs(b.lagMin));
  // one outlet's take per story: near-identical titles were folded at the edge already
  return out.slice(0, limit);
}

// ---------------------------------------------------------------- stories

export type Confidence = "strong" | "likely" | "possible" | "none";

export type Story = {
  id: string;
  /** The strongest move, and every market that moved with it for the same reason. */
  lead: Move;
  moves: Move[];
  markets: Market[];
  causes: Cause[];
  confidence: Confidence;
  /** What the front page ranks by. */
  impact: number;
  class: AssetClass;
  /** Every class that moved, the lead's first: more than one is a cross-asset story. */
  classes: AssetClass[];
  place: Place | null;
  /** Newest edge of the story, ms. */
  at: number;
};

export const confidenceOf = (score: number | undefined): Confidence =>
  score === undefined ? "none" : score >= 0.8 ? "strong" : score >= 0.5 ? "likely" : score >= 0.25 ? "possible" : "none";

function impactOf(moves: Move[], now: number): number {
  const lead = moves[0] as Move;
  const size = zOf(lead) * Math.log10(10 + lead.notional / 50_000);
  const breadth = moves.slice(1).reduce((sum, move) => sum + zOf(move) * 0.35, 0);
  const ageHours = Math.max(0, (now - lead.end) / 3_600_000);
  return (size + breadth) * (0.35 + 0.65 / (1 + ageHours / 3));
}

type Entry = { move: Move; market: Market; causes: Cause[] };

/** Moves that start this close together may be one event. */
const TOGETHER_MS = 6 * 60_000;
/** The markets a cross-asset shock always shows up in. */
const BENCHMARKS = new Set(["BTC", "ETH", "SP500", "NDX", "XYZ100", "XAU", "CL", "BZ", "BRENTOIL", "EUR", "JPY", "US10Y", "10Y"]);
const isBenchmark = (market: Market) => BENCHMARKS.has(market.underlying) || BENCHMARKS.has(market.symbol);
/** A move's own cause this strong and this specific keeps it out of a bigger story. */
const OWN_CAUSE = 0.75;

const weightOf = (entry: Entry) => zOf(entry.move) * Math.log10(10 + entry.move.notional / 50_000);

/** The headlines that explain a group of moves best: each scored by how well it explains every
 * member, weighted by the member's size. A cause that explains one coin of twenty scores low. */
function groupCauses(group: Entry[]): Cause[] {
  const total = group.reduce((sum, entry) => sum + weightOf(entry), 0) || 1;
  const byItem = new Map<string, { cause: Cause; sum: number }>();
  for (const entry of group) {
    const weight = weightOf(entry);
    for (const cause of entry.causes) {
      const held = byItem.get(cause.item.id);
      if (held) held.sum += cause.score * weight;
      else byItem.set(cause.item.id, { cause, sum: cause.score * weight });
    }
  }
  return [...byItem.values()]
    .map(({ cause, sum }) => ({ ...cause, score: sum / total }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
}

/** Groups moves into stories. Moves that start within minutes of each other are one event (a
 * data print moves stocks, rates, gold and crypto in the same five minutes); so are moves that
 * share their likeliest headline. A move with a strong cause of its own that the group's lead
 * does not share stays a story of its own. */
export function storiesOf(moves: Entry[], now: number): Story[] {
  const sorted = [...moves].sort((a, b) => weightOf(b) - weightOf(a));
  const groups: Entry[][] = [];
  for (const entry of sorted) {
    const top = entry.causes[0];
    const home = groups.find((group) => {
      const lead = group[0] as Entry;
      const leadTop = lead.causes[0];
      const sameCause = top !== undefined && leadTop !== undefined && top.item.id === leadTop.item.id;
      if (sameCause && Math.abs(entry.move.start - lead.move.start) <= 45 * 60_000) return true;
      if (Math.abs(entry.move.start - lead.move.start) > TOGETHER_MS) return false;
      // older news about one market does not explain why it moved in the same minutes as everything else
      const stale = top !== undefined && leadTop !== undefined && leadTop.score >= 0.5 && top.item.ts < leadTop.item.ts - 10 * 60_000;
      const ownStory = top !== undefined && !stale && top.score >= OWN_CAUSE && top.reasons[0]?.startsWith("names") && relevance(top.item, lead.market).score < 0.3;
      if (ownStory) return false;
      // across classes only when a benchmark moved too: a real shock always shows up in one
      return entry.market.class === lead.market.class || group.some((member) => isBenchmark(member.market)) || isBenchmark(entry.market);
    });
    if (home) {
      // one move per market in a story: the bigger one, already in
      if (!home.some((member) => member.market.coin === entry.market.coin)) home.push(entry);
    } else groups.push([entry]);
  }
  return groups.map((group) => {
    const lead = group[0] as Entry;
    // a headline that names the lead and fits it better than anything fits the group is the story
    const own = lead.causes[0];
    const grouped = group.length > 1 ? groupCauses(group) : lead.causes;
    const named = own !== undefined && own.reasons[0]?.startsWith("names") && own.score > (grouped[0]?.score ?? 0);
    const causes = named ? [own, ...grouped.filter((cause) => cause.item.id !== own.item.id)].slice(0, 5) : grouped;
    const place = causes.find((cause) => cause.item.tags.place)?.item.tags.place ?? null;
    const moves = group.map((entry) => entry.move);
    return {
      id: lead.move.id,
      lead: lead.move,
      moves,
      markets: group.map((entry) => entry.market),
      causes,
      confidence: confidenceOf(causes[0]?.score),
      impact: impactOf(moves, now),
      class: lead.market.class,
      classes: [...new Set(group.map((entry) => entry.market.class))],
      place,
      at: Math.max(...moves.map((move) => move.end)),
    };
  });
}

// ---------------------------------------------------------------- reactions

export type Reaction = {
  coin: string;
  symbol: string;
  /** Since the headline. */
  ret: number;
  /** In units of the market's volatility over that span. */
  z: number;
  /** How much the headline is about it. */
  relevance: number;
};

/** The markets a headline reaches, most relevant first, each with what it has done since. */
export function reactionsOf(item: WireItem, universe: Universe, held: Record<string, Series>, now: number, limit = 3): Reaction[] {
  const seen = new Set<string>();
  const out: Reaction[] = [];
  for (const underlying of item.tags.underlyings) {
    const market = universe.byUnderlying.get(underlying)?.[0];
    if (!market || seen.has(market.coin)) continue;
    seen.add(market.coin);
    const bars = held[market.coin]?.bars;
    if (!bars || bars.length < 30) continue;
    const at = priceAtBars(bars, item.ts);
    const last = bars[bars.length - 1] as Bar;
    if (at === null || !(at > 0)) continue;
    const span = Math.max(1, (now - item.ts) / BAR_MS);
    const ret = last.c / at - 1;
    out.push({ coin: market.coin, symbol: market.symbol, ret, z: Math.log(last.c / at) / (sigmaOf(bars) * Math.sqrt(span)), relevance: relevance(item, market).score });
    if (out.length >= limit) break;
  }
  return out;
}

function priceAtBars(bars: Bar[], ms: number): number | null {
  if (!bars.length || (bars[0] as Bar).t > ms) return null;
  let lo = 0;
  let hi = bars.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if ((bars[mid] as Bar).t <= ms) lo = mid;
    else hi = mid - 1;
  }
  const bar = bars[lo] as Bar;
  return ms < bar.t + BAR_MS ? bar.o : bar.c;
}

/** A strong headline whose markets have not moved yet. */
export type Waiting = { item: WireItem; reactions: Reaction[] };

/** Words of a headline that should move a price: something happened, not something was said. */
const IMPACT =
  /\b(strikes?|attack\w*|missiles?|drones?|explosion|blasts?|seiz\w*|sanction\w*|tariffs?|ban(s|ned)?|bans?|halt\w*|outage|hack\w*|exploit\w*|drain\w*|bankrupt\w*|default\w*|delist\w*|lawsuit|sues?|sued|charges?|charged|indict\w*|approv\w*|reject\w*|earnings|revenue|guidance|profit warning|downgrad\w*|upgrad\w*|cuts?|hikes?|raises?|acquir\w*|merger|takeover|buyback|recall|resign\w*|fired|ceasefire|invasion|blockade|shutdown|lists?|listing|ETF)\b/i;

export function waitingOf(wire: WireItem[], reactions: Map<string, Reaction[]>, now: number): Waiting[] {
  const out: Waiting[] = [];
  for (const item of wire) {
    const age = now - item.ts;
    if (age < 0 || age > 3 * 3_600_000) continue;
    if (!IMPACT.test(item.title)) continue;
    const linked = (reactions.get(item.id) ?? []).filter((reaction) => reaction.relevance >= 0.7);
    if (!linked.length) continue;
    if (linked.every((reaction) => Math.abs(reaction.z) < 1.2)) out.push({ item, reactions: linked });
  }
  return out.slice(0, 12);
}

// ---------------------------------------------------------------- what to watch

/** The markets worth a day of candles, most important first: the majors of every class, what the
 * wire names, then the day's biggest movers and most traded. */
export function watchList(universe: Universe, wire: WireItem[], now: number, limit = 48): string[] {
  const out: string[] = [];
  const add = (coin: string | undefined) => {
    if (coin && !out.includes(coin) && universe.byCoin.has(coin)) out.push(coin);
  };
  const first = (underlying: string) => universe.byUnderlying.get(underlying)?.[0]?.coin;
  for (const underlying of ["BTC", "ETH", "SOL", "HYPE", "SP500", "XAU", "CL", "NDX", "XAG", "NVDA", "BZ", "EUR", "JPY"]) add(first(underlying));
  const mentions = new Map<string, number>();
  for (const item of wire) {
    if (now - item.ts > 12 * 3_600_000) continue;
    for (const underlying of item.tags.underlyings.slice(0, 3)) {
      const coin = first(underlying);
      if (coin) mentions.set(coin, (mentions.get(coin) ?? 0) + 1);
    }
  }
  [...mentions.entries()].sort((a, b) => b[1] - a[1]).slice(0, 14).forEach(([coin]) => add(coin));
  const liquid = universe.markets.filter((market) => market.dayVolumeUsd >= (market.class === "crypto" ? 3_000_000 : 400_000));
  [...liquid].sort((a, b) => Math.abs(b.change24h) - Math.abs(a.change24h)).slice(0, 14).forEach((market) => add(market.coin));
  liquid.slice(0, 30).forEach((market) => add(market.coin));
  return out.slice(0, limit);
}

// ---------------------------------------------------------------- the edition

export type Edition = {
  at: number;
  stories: Story[];
  reactions: Map<string, Reaction[]>;
  waiting: Waiting[];
  scanned: { markets: number; measured: number; headlines: number };
};

export function buildEdition(universe: Universe, wire: WireItem[], held: Record<string, Series>, now: number): Edition {
  const moves: { move: Move; market: Market; causes: Cause[] }[] = [];
  let measured = 0;
  for (const [coin, entry] of Object.entries(held)) {
    const market = universe.byCoin.get(coin);
    if (!market) continue;
    // one listing per underlying: its most traded
    if (universe.byUnderlying.get(market.underlying)?.[0]?.coin !== coin) continue;
    measured += 1;
    for (const move of detect(market, entry.bars, now)) {
      moves.push({ move, market, causes: causesOf(move, market, wire) });
    }
  }
  const stories = storiesOf(moves, now).sort((a, b) => b.impact - a.impact);
  const reactions = new Map<string, Reaction[]>();
  for (const item of wire.slice(0, 400)) reactions.set(item.id, reactionsOf(item, universe, held, now));
  return {
    at: now,
    stories,
    reactions,
    waiting: waitingOf(wire, reactions, now),
    scanned: { markets: universe.markets.length, measured, headlines: wire.length },
  };
}
